/* eslint-disable */
// src/components/visit/MachinesTableGroup.js

import React, { useCallback, useMemo, useState } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import IndividualMachineEditorLayout from "components/visit/IndividualMachineEditorLayout";
import buildVisitTable from "tables/buildVisitTables";
import PropTypes from "prop-types";
// Defaults dinámicos para números y moneda
import { nfDynamic as defaultNf, moneyFmtDynamic as defaultMoneyFmt } from "utils/formatters";

/* opcional — si quisieras aplicar el skin desde dentro.
   En esta implementación lo aplicamos desde afuera con <Box sx={getTableSkin(type)>,
   pero dejo el import y soporte de prop `skinType` por si lo prefieres usar interno. */
import { getTableSkin } from "ui/skins/typeBlocks";

/* File: src/components/visit/MachinesTableGroup.jsx */
export default function MachinesTableGroup({
  title,
  machinesArray = [],
  visitMachineDetails = [],
  userInputs = {},
  settledInputFields = {},
  handleInputChange = () => {},
  handleToggleReset = () => {},
  wasCollectedByLM = {},
  handleToggleWasCollected = () => {},
  counterVerificationContextsByLocalMachineId = {},
  isLoading = false,
  compact = false,
  // inyectables (opcionales)
  nf = defaultNf,
  moneyFmt = defaultMoneyFmt,
  lastGlobalVisitId = null,
  /* (opcional) — si lo pasas, aplica el skin adentro */
  skinType = null,
  /* override de títulos de columnas (p.ej., { In: "Entrada" }) */
  columnTitles = undefined,
  /* ocultar columna Total vía esquema */
  hideTotal = false,
  individualEditorLayout = false,
}) {
  const [
    counterVerificationExpandedByLocalMachineId,
    setCounterVerificationExpandedByLocalMachineId,
  ] = useState({});
  const isCounterVerificationExpanded = useCallback(
    (localMachineId) =>
      Boolean(counterVerificationExpandedByLocalMachineId?.[Number(localMachineId)]),
    [counterVerificationExpandedByLocalMachineId]
  );
  const toggleCounterVerificationExpanded = useCallback((localMachineId) => {
    const key = Number(localMachineId);
    setCounterVerificationExpandedByLocalMachineId((prev) => ({
      ...prev,
      [key]: !prev?.[key],
    }));
  }, []);

  const getLastVisit = useMemo(() => {
    const dict = Object.create(null);
    (visitMachineDetails || []).forEach((v) => {
      dict[v.LocalMachineId] = v;
    });
    return (lmId) => dict[lmId] || {};
  }, [visitMachineDetails]);

  // Deducimos el tipo para skin por si no viene explícito
  const typeGuess = useMemo(() => {
    if (!machinesArray?.length) return null;
    return machinesArray[0]?.MachineType?.Type || machinesArray[0]?.MachineType || "Default";
  }, [machinesArray]);

  const table = useMemo(() => {
    if (!machinesArray.length) return { columns: [], rows: [] };

    const type = machinesArray[0]?.MachineType?.Type || machinesArray[0]?.MachineType || "Otro";

    return buildVisitTable({
      mode: "edit",
      type,
      items: machinesArray,
      helpers: {
        nf,
        moneyFmt,
        getLastVisit,
        computeKey: (m) => m?.LocalMachineId ?? m?.Id,
        userInputs,
        settledInputFields,
        handleInputChange,
        handleToggleReset,
        wasCollectedByLM,
        handleToggleWasCollected,
        counterVerificationContextsByLocalMachineId,
        isCounterVerificationExpanded,
        toggleCounterVerificationExpanded,
        lastGlobalVisitId,
      },
      options: { compact, columnTitles, hideTotal, individualEditorLayout },
    });
  }, [
    machinesArray,
    nf,
    moneyFmt,
    getLastVisit,
    userInputs,
    settledInputFields,
    handleInputChange,
    handleToggleReset,
    wasCollectedByLM,
    handleToggleWasCollected,
    counterVerificationContextsByLocalMachineId,
    isCounterVerificationExpanded,
    toggleCounterVerificationExpanded,
    lastGlobalVisitId,
    compact,
    columnTitles,
    hideTotal, // ⬅️ dependencia
    individualEditorLayout,
  ]);

  // si viene skinType, aplicamos el skin desde acá; de lo contrario, el caller puede envolver con <Box sx={getTableSkin(...)} />
  const wrapperSx = getTableSkin(skinType || typeGuess || "Default");

  return (
    <MDBox mt={title ? 2 : 0} sx={wrapperSx}>
      {title && (
        <MDTypography variant="subtitle1" color="text" sx={{ mb: 1 }}>
          {title}
        </MDTypography>
      )}
      {individualEditorLayout ? (
        <IndividualMachineEditorLayout
          table={table}
          type={typeGuess || "Default"}
          rented={hideTotal}
          isLoading={isLoading}
        />
      ) : (
        <DataTable
          table={table}
          isSorted={false}
          entriesPerPage={false}
          canSearch={false}
          showTotalEntries={false}
          loading={isLoading}
        />
      )}
    </MDBox>
  );
}

MachinesTableGroup.propTypes = {
  title: PropTypes.string,
  machinesArray: PropTypes.array,
  visitMachineDetails: PropTypes.array,
  userInputs: PropTypes.object,
  settledInputFields: PropTypes.object,
  handleInputChange: PropTypes.func,
  handleToggleReset: PropTypes.func,
  wasCollectedByLM: PropTypes.object,
  handleToggleWasCollected: PropTypes.func,
  counterVerificationContextsByLocalMachineId: PropTypes.object,
  isLoading: PropTypes.bool,
  compact: PropTypes.bool,
  nf: PropTypes.object,
  moneyFmt: PropTypes.object,
  lastGlobalVisitId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  skinType: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  columnTitles: PropTypes.object,
  hideTotal: PropTypes.bool,
  individualEditorLayout: PropTypes.bool,
};
